import { useState, useCallback } from 'react';
import WordOptimizer from '../utils/WordOptimizer.js';

/**
 * React hook for word optimization functionality
 * Provides real-time word suggestions and optimal placement strategies
 */
export default function useWordOptimizer(validateWord) {
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [lastAnalysis, setLastAnalysis] = useState(null);

  /**
   * Find optimal word placement
   */
  const findOptimalPlacement = useCallback(async (letters, spellingBar, modifiers, options = {}) => {
    if (!validateWord) {
      return {
        bestWord: null,
        bestPlacement: null,
        bestScore: 0,
        alternatives: [],
        error: 'Dictionary not available'
      };
    }

    setIsAnalyzing(true);

    try {
      // Use setTimeout to prevent UI blocking for complex analysis
      const result = await new Promise((resolve) => {
        setTimeout(() => {
          const analysis = WordOptimizer.findOptimalPlacement(
            letters,
            spellingBar,
            modifiers,
            validateWord,
            options
          );
          resolve(analysis);
        }, 0);
      });

      setLastAnalysis(result);
      return result;
    } catch (error) {
      console.error('Word optimization error:', error);
      return {
        bestWord: null,
        bestPlacement: null,
        bestScore: 0,
        alternatives: [],
        error: error.message
      };
    } finally {
      setIsAnalyzing(false);
    }
  }, [validateWord]);

  /**
   * Get quick suggestions for real-time hints
   */
  const getQuickSuggestions = useCallback((letters, spellingBar, modifiers) => {
    if (!validateWord) return null;

    return WordOptimizer.getQuickSuggestions(letters, spellingBar, modifiers, validateWord);
  }, [validateWord]);

  /**
   * Analyze current game state and provide suggestions
   */
  const analyzeCurrentState = useCallback((letters, spellingBar, modifiers) => {
    return findOptimalPlacement(letters, spellingBar, modifiers, {
      maxSuggestions: 5,
      minWordLength: 2,
      maxWordLength: 10,
      includePartialWords: spellingBar.some(slot => slot !== null)
    });
  }, [findOptimalPlacement]);

  return {
    findOptimalPlacement,
    getQuickSuggestions,
    analyzeCurrentState,
    isAnalyzing,
    lastAnalysis
  };
}